import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useCallback } from "react";

import {
  CloudSettings,
  EmptystateCloudColored,
  LogoAwsColored,
  LogoAzureColored,
  LogoGcpColored,
} from "components/icons/generated";
import TileCheckboxGrid from "components/TileCheckboxGrid";
import Box from "ds/components/Box";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import useCanCreateCloudIntegration from "shared/CloudIntegration/access/useCanCreateCloudIntegration";
import { SCAN_INTEGRATION_FORM_MESSAGE } from "shared/Scan/constants";
import tooltips from "shared/Scan/tooltips";
import { ScanFormFields } from "shared/Scan/types";
import { getDocsUrl } from "utils/getDocsUrl";
import { showAwsIntegrationFormDrawer } from "views/Account/Integrations/registry/AWS/components/FormDrawer";

import { SCHEDULE_SCAN_WIZARD_STEP_HEADERS } from "../../constants";
import { NewScanScheduleContext } from "../../context";
import NewScanScheduleRequirementEmptyState from "../../components/RequirementEmptyState";
import { getMissingScanRequirements } from "../../helpers";
import { NewScanScheduleStep } from "../../types";
import IntegrationField from "./IntegrationField";
import ProviderTile, { ProviderTileProps } from "./ProviderTile";
import RegionsField from "./RegionsField";

const PROVIDERS: ProviderTileProps[] = [
  { name: "AWS", icon: LogoAwsColored, selected: true },
  { name: "Azure", icon: LogoAzureColored, comingSoon: true },
  { name: "GCP", icon: LogoGcpColored, comingSoon: true },
  { name: "Custom", icon: CloudSettings, comingSoon: true },
];

const NewScanScheduleStepAttachProviders = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<ScanFormFields>();

  const {
    goToStep,
    formOptions,
    formOptions: {
      providerVersions,
      regions,
      integrations,
      loading,
      error,
      refetch: refetchFormOptions,
    },
  } = useTypedContext(NewScanScheduleContext);

  const space = useWatch({ control, name: "space" });
  const trackAnalytics = useAnalytics();
  const canCreateCloudIntegration = useCanCreateCloudIntegration(space);

  const { hasNoAttachableIntegration } = getMissingScanRequirements(formOptions, space);

  const handleSetUpIntegration = useCallback(() => {
    showAwsIntegrationFormDrawer({
      trackAnalytics,
      initialSpaceId: space,
      onSuccess: () => refetchFormOptions(),
      formMessage: SCAN_INTEGRATION_FORM_MESSAGE,
    });
  }, [trackAnalytics, space, refetchFormOptions]);

  const handleChangeSpace = useCallback(() => {
    goToStep(NewScanScheduleStep.ScanDetails);
  }, [goToStep]);

  const providerVersionOptions = providerVersions.map((version) => ({
    value: version,
    label: version,
  }));

  const providerVersionHelperText = error ? "Could not load provider versions." : undefined;
  const regionsHelperText = error ? "Could not load regions." : undefined;
  const integrationHelperText = error ? "Could not load integrations." : undefined;

  return (
    <FullScreenModalBody>
      <FullScreenModalBodyHeader
        {...SCHEDULE_SCAN_WIZARD_STEP_HEADERS[NewScanScheduleStep.AttachProviders]}
      />

      <Box direction="column" fullWidth>
        <TileCheckboxGrid columns={4}>
          {PROVIDERS.map((provider) => (
            <ProviderTile key={provider.name} {...provider} />
          ))}
        </TileCheckboxGrid>

        {hasNoAttachableIntegration && (
          <Box margin="x-large 0 0">
            <NewScanScheduleRequirementEmptyState
              icon={EmptystateCloudColored}
              title="No AWS integrations in selected space"
              caption="Spacelift uses a cloud integration to generate short-lived credentials and read resources from your AWS account. To continue, set up an integration in this space or go back and choose a different space."
              primaryActionLabel="Set up"
              primaryAction={canCreateCloudIntegration}
              onPrimaryAction={handleSetUpIntegration}
              onChangeSpace={handleChangeSpace}
              documentationUrl={getDocsUrl("/integrations/cloud-providers")}
            />
          </Box>
        )}

        {!hasNoAttachableIntegration && (
          <Box direction="column" margin="x-large 0 0">
            <Controller
              name="providerVersion"
              control={control}
              rules={{ required: "Provider version is required." }}
              render={({ field }) => (
                <ComboBox
                  label="Provider version"
                  tooltipInfo={tooltips.PROVIDER_VERSION}
                  error={errors?.providerVersion?.message}
                  helperText={providerVersionHelperText}
                  value={field.value}
                  items={providerVersionOptions}
                  isLoading={loading}
                  onChange={(value) => field.onChange(value ?? "")}
                  placeholder="Select a provider version"
                >
                  {(item) => <ComboBoxDefaultItem id={item.value} {...item} />}
                </ComboBox>
              )}
            />

            <Controller
              name="integrationId"
              control={control}
              rules={{ required: "Integration is required." }}
              render={({ field }) => (
                <IntegrationField
                  value={field.value}
                  onChange={field.onChange}
                  options={integrations}
                  isLoading={loading}
                  error={errors?.integrationId?.message}
                  helperText={integrationHelperText}
                />
              )}
            />

            <Controller
              name="regions"
              control={control}
              rules={{
                validate: (value) => value.length > 0 || "Select at least one region.",
              }}
              render={({ field }) => (
                <RegionsField
                  value={field.value}
                  onChange={field.onChange}
                  options={regions}
                  isLoading={loading}
                  error={errors?.regions?.message}
                  helperText={regionsHelperText}
                />
              )}
            />
          </Box>
        )}
      </Box>
    </FullScreenModalBody>
  );
};

export default NewScanScheduleStepAttachProviders;
